import {
  AlertTriangle,
  MapPin,
  MessageSquare,
  Plus,
  Sparkles,
  UserX,
} from 'lucide-react'
import type { ReactNode } from 'react'

import { Badge, cn } from '@aeris/ui'

import type { InboxTag, InboxTagKind } from '../lib/inbox.ts'
import { useWorkspace } from '../lib/workspace.ts'

/**
 * Os marcadores do cabeçalho da conversa — F087.
 *
 * Quem decide QUAIS marcadores existem é `inboxTagsOf`, em `lib/inbox.ts`, e é
 * lá que os testes moram: a regra é lógica pura, e uma regra dessas testada
 * através do DOM é testada pela metade — o jsdom não faz layout, então a
 * renderização aqui não prova largura nem quebra de linha de qualquer forma.
 *
 * Este arquivo só desenha: ícone, tom, e se o marcador é uma porta ou só uma
 * informação.
 */

const ICONS: Record<InboxTagKind, ReactNode> = {
  'no-party': <UserX aria-hidden="true" className="h-3 w-3" />,
  'archived-party': <UserX aria-hidden="true" className="h-3 w-3" />,
  city: <MapPin aria-hidden="true" className="h-3 w-3" />,
  'add-city': <Plus aria-hidden="true" className="h-3 w-3" />,
  overdue: <AlertTriangle aria-hidden="true" className="h-3 w-3" />,
  'new-customer': <Sparkles aria-hidden="true" className="h-3 w-3" />,
  // O canal (F091): o mesmo ícone que a barra lateral usa para Atendimento,
  // porque é a mesma ideia — "isto é uma conversa de WhatsApp" — e reaproveitar
  // o símbolo poupa a pessoa de aprender um segundo.
  channel: <MessageSquare aria-hidden="true" className="h-3 w-3" />,
  /*
   * As manuais (F088) não ganham ícone. O ícone das automáticas existe para
   * dizer DE ONDE elas vêm — um alfinete é cidade, um triângulo é atraso — e
   * um marcador escrito por uma pessoa não vem de lugar nenhum além da
   * decisão dela. Um ícone genérico ali só gastaria os doze pixels que o nome
   * precisa.
   */
  'party-tag': null,
  'conversation-tag': null,
}

/**
 * Quantas cabem antes do contador.
 *
 * Um número fixo, e não uma medição: o jsdom não faz layout, então medir seria
 * uma regra que teste nenhum alcança — e um contador que aparece de forma
 * determinística é o que permite dizer "+2" com honestidade em vez de deixar a
 * último marcador cortado pela metade.
 */
const MAX_VISIBLE = 4

/**
 * O rótulo visível é `aria-hidden` e o `srLabel` toma o lugar dele.
 *
 * Não são a mesma frase de propósito: `Ponta Grossa/PR` ao lado de um alfinete
 * se explica sozinho para quem enxerga, e sozinho num fluxo de leitura é um
 * nome solto no meio do cabeçalho.
 */
const TagBody = ({ tag }: { tag: InboxTag }): ReactNode => (
  <>
    {ICONS[tag.kind]}
    <span className="sr-only">{tag.srLabel}</span>
    <span aria-hidden="true">{tag.label}</span>
  </>
)

const INTERACTIVE =
  'rounded-(--radius-full) focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-(--border-focus)'

export type InboxTagsProps = {
  tags: readonly InboxTag[]
  /** Para onde o marcador `+ Cidade` leva. */
  partyId: string | null
  /** Abre o painel de detalhes — nunca fecha, mesmo se já estiver aberto. */
  onOpenPanel: () => void
  className?: string
}

export const InboxTags = ({
  tags,
  partyId,
  onOpenPanel,
  className,
}: InboxTagsProps): ReactNode => {
  const workspace = useWorkspace()

  if (tags.length === 0) return null

  const visible = tags.slice(0, MAX_VISIBLE)
  const hidden = tags.slice(MAX_VISIBLE)

  return (
    /*
     * `overflow-hidden` e não `flex-wrap`: o cabeçalho tem altura fixa, e uma
     * segunda fileira de marcadores empurraria o nome para fora dele. Quando
     * não couberem, as últimas somem — e a ordem em que `inboxTagsOf` as
     * devolve é a ordem de importância, então o que some é o menos urgente.
     */
    <span
      className={cn(
        'flex min-w-0 items-center gap-1.5 overflow-hidden',
        className
      )}
    >
      {visible.map((tag) => {
        if (tag.action === 'panel') {
          return (
            <button
              key={tag.id}
              type="button"
              onClick={onOpenPanel}
              className={cn(INTERACTIVE, 'shrink-0')}
            >
              <Badge
                tone={tag.tone}
                className="cursor-pointer hover:brightness-95"
              >
                <TagBody tag={tag} />
              </Badge>
            </button>
          )
        }

        /*
         * `+ Cidade` leva ao CADASTRO, e não a um formulário de endereço aqui
         * dentro. Não é preguiça: `party_address` exige logradouro
         * (`party_address_street_not_blank`), então "só a cidade" não é um
         * endereço que o banco aceite — e um formulário reduzido no inbox
         * ficaria sem CEP, sem código IBGE e sem a validação que o cadastro já
         * tem. O caminho certo é o que já existe.
         */
        if (tag.action === 'party' && partyId !== null) {
          return (
            <button
              key={tag.id}
              type="button"
              /*
               * `workspace.open` e não um `<Link>` (F089).
               *
               * Duas coisas que o link cru não fazia: no Modo Site ele leva o
               * `voltar`, então fechar o cadastro devolve a pessoa à conversa
               * em vez de deixá-la parada em Pessoas; e no Modo PC a mesma
               * chamada abre uma JANELA, e o atendimento nem sai da tela.
               */
              onClick={() => {
                workspace.open({
                  module: 'pessoas',
                  entityId: partyId,
                  returnTo: 'atendimento',
                })
              }}
              className={cn(INTERACTIVE, 'shrink-0')}
            >
              <Badge
                tone={tag.tone}
                className="cursor-pointer border-dashed text-(--text-muted) hover:brightness-95"
              >
                <TagBody tag={tag} />
              </Badge>
            </button>
          )
        }

        return (
          <Badge key={tag.id} tone={tag.tone} className="shrink-0">
            <TagBody tag={tag} />
          </Badge>
        )
      })}

      {/*
       * O que não coube, contado. `title` porque a informação é secundária: ela
       * responde "estou perdendo alguma coisa?" sem ocupar a fileira, e o
       * painel de detalhes tem todas.
       */}
      {hidden.length === 0 ? null : (
        <Badge
          className="shrink-0"
          title={hidden.map((tag) => tag.label).join(', ')}
        >
          <span className="sr-only">
            Mais {String(hidden.length)}:{' '}
            {hidden.map((tag) => tag.label).join(', ')}
          </span>
          <span aria-hidden="true">+{String(hidden.length)}</span>
        </Badge>
      )}
    </span>
  )
}
